import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Box,
  Typography,
  Button,
  Select,
  TextField,
  Spinner,
  Alert,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import {
  adminApiClient,
  AdminApiError,
  type VendorApplicationDetail,
  type VendorBusinessInfo,
} from '../lib/adminApiClient';
import { NEUTRAL_BTN, DANGER_BTN } from '../lib/buttonStyles';
import { StoreChip, StageChip, Pill } from '../components/VendorChips';
import { PIPELINE_STAGES, stageIndex, stageChip, type VendorAppStatus } from '../lib/vendorChips';
import { canUnlockReapplication } from '../lib/vendorApplicationLifecycle';

/**
 * Vendor application — review / pipeline detail (Figma 295:306). Walks one
 * application through the onboarding stages (each stage = an action, incl.
 * skip-visit), shows the applicant's business + onboarding detail, and
 * approves → provisions the Vendor.
 */

const GREEN = '#2E9E3A';
const TERMINAL = ['approved', 'declined', 'withdrawn'];

const DATA_SHAPE_OPTIONS = [
  { value: 'square_oauth', label: 'Square (OAuth)' },
  { value: 'excel', label: 'Excel export' },
  { value: 'csv', label: 'CSV upload' },
  { value: 'manual', label: 'Manual entry' },
  { value: 'custom', label: 'Custom adapter' },
];

function fmtDate(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
}
function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}

export default function VendorApplicationReview() {
  const caps = useCapabilities();
  const canView = caps.has('vendor:read');
  const canReview = caps.has('vendor:review');
  const canManage = caps.has('vendor:manage');
  const navigate = useNavigate();
  const { id = '' } = useParams();

  const [app, setApp] = useState<VendorApplicationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [busy, setBusy] = useState(false);

  // per-stage action inputs
  const [dataShape, setDataShape] = useState('excel');
  const [visitAt, setVisitAt] = useState('');
  const [declineOpen, setDeclineOpen] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.getVendorApplication(id);
      setApp(res.application);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (canView && id) void load();
  }, [canView, id, load]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      setFlash({ kind: 'success', msg: ok });
      setDeclineOpen(false);
      setReason('');
      await load();
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof AdminApiError ? err.message : 'Action failed' });
    } finally {
      setBusy(false);
    }
  };

  if (caps.loading || loading)
    return (
      <Centered>
        <Spinner color="brand" />
      </Centered>
    );
  if (!canView)
    return <Alert severity="error">You don’t have permission to view vendor applications.</Alert>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!app) return <Alert severity="warning">Application not found.</Alert>;

  const bi: VendorBusinessInfo = app.businessInfo ?? {};
  const name = bi.businessName || 'Unnamed vendor';
  const location = [bi.address?.city, bi.address?.state].filter(Boolean).join(', ') || '—';
  const isTerminal = TERMINAL.includes(app.status);

  return (
    <Box>
      {/* Back link */}
      <Typography
        onClick={() => navigate('/vendor-applications')}
        sx={{ color: '#1f7a3d', fontWeight: 600, fontSize: 13, cursor: 'pointer', mb: 1.5 }}
      >
        ‹ Vendor applications
      </Typography>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937', mb: 0.5 }}>
            {name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
            <StoreChip businessType={bi.businessType} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {location}
            </Typography>
            <Typography variant="body2" sx={{ color: '#9aa3ab' }}>
              · Applied {fmtDate(app.createdAt)}
            </Typography>
            {isTerminal && <StageChip status={app.status} />}
          </Box>
        </Box>
        {canReview && !isTerminal && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              ariaLabel="Decline application"
              buttonText="Decline"
              variant="outlined"
              size="small"
              sx={DANGER_BTN}
              disabled={busy}
              onClick={() => setDeclineOpen((v) => !v)}
            />
            <Button
              ariaLabel="Approve application"
              buttonText="Approve"
              variant="contained"
              size="small"
              disabled={busy || app.status !== 'vendor_confirmed'}
              onClick={() => run(() => adminApiClient.approveVendorApplication(app.applicationId), 'Vendor approved & provisioned.')}
            />
          </Box>
        )}
        {canUnlockReapplication(app, canManage) && (
          <Button
            ariaLabel="Unlock reapplication"
            buttonText="Unlock reapplication"
            variant="contained"
            size="small"
            disabled={busy}
            onClick={() => run(
              () => adminApiClient.unlockVendorApplication(app.applicationId),
              'Reapplication unlocked and the applicant account was re-enabled.',
            )}
          />
        )}
      </Box>

      {!canReview && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can browse but can’t make changes.</Alert>
        </Box>
      )}

      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind}>{flash.msg}</Alert>
        </Box>
      )}

      {app.duplicateBusinessWarning && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning">
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              Possible duplicate business
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.75 }}>
              The normalized business name and address or phone match {app.duplicateBusinessWarning.matchCount}{' '}
              application{app.duplicateBusinessWarning.matchCount === 1 ? '' : 's'} from a different email. This is a warning only.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {app.duplicateBusinessWarning.matches.map((match) => (
                <Typography
                  key={match.applicationId}
                  variant="body2"
                  onClick={() => navigate(`/vendor-applications/${match.applicationId}`)}
                  sx={{ color: '#7A4B00', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Review {match.applicantEmail ?? 'matching application'} ({match.matchedOn.join(' + ')})
                </Typography>
              ))}
            </Box>
          </Alert>
        </Box>
      )}

      {app.reapplyUnlockedAt && (app.status === 'declined' || app.status === 'withdrawn') && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="success">
            Reapplication unlocked {fmtDate(app.reapplyUnlockedAt)}. The applicant account is enabled and may apply again now.
          </Alert>
        </Box>
      )}

      {/* Decline reason */}
      {canReview && declineOpen && (
        <Box sx={{ mb: 2, p: 2, border: '1px solid #e6b0aa', borderRadius: 2, backgroundColor: '#fdf2f1' }}>
          <Typography sx={{ fontWeight: 700, color: '#C0392B', mb: 1 }}>Decline this application</Typography>
          <TextField
            label="Reason (shared internally)"
            ariaLabel="Decline reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            fullWidth
            multiline
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button ariaLabel="Cancel decline" buttonText="Cancel" variant="outlined" size="small" sx={NEUTRAL_BTN} disabled={busy} onClick={() => setDeclineOpen(false)} />
            <Button
              ariaLabel="Confirm decline"
              buttonText="Confirm decline"
              variant="contained"
              size="small"
              disabled={busy || !reason.trim()}
              onClick={() => run(() => adminApiClient.transitionVendorApplication(app.applicationId, { action: 'decline', reason: reason.trim() }), 'Application declined.')}
            />
          </Box>
        </Box>
      )}

      {/* Pipeline stepper */}
      <Stepper app={app} />

      {/* Current-stage action */}
      {canReview && !isTerminal && (
        <ActionCard
          status={app.status}
          busy={busy}
          dataShape={dataShape}
          setDataShape={setDataShape}
          visitAt={visitAt}
          setVisitAt={setVisitAt}
          onAdvance={run}
          appId={app.applicationId}
        />
      )}

      {/* Detail panels */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <BusinessPanel app={app} />
        <OnboardingPanel app={app} />
      </Box>
    </Box>
  );
}

// ─── Pipeline stepper ─────────────────────────────────────────────────────────
function Stepper({ app }: { app: VendorApplicationDetail }) {
  const doneThrough = stageIndex(app.status); // -1 for terminal
  const subFor = (status: VendorAppStatus): string => {
    switch (status) {
      case 'submitted':
        return fmtDate(app.createdAt);
      case 'data_shape_assigned':
        return app.dataShapeAssigned ?? '';
      case 'staff_visit_scheduled':
        return app.staffVisitScheduledAt ? fmtDate(app.staffVisitScheduledAt) : doneThrough >= 3 ? 'Skipped' : '';
      case 'adapter_built':
        return fmtDate(app.adapterBuiltAt);
      case 'vendor_confirmed':
        return fmtDate(app.vendorConfirmedAt);
      case 'approved':
        return fmtDate(app.reviewerDecisionAt);
      default:
        return '';
    }
  };

  return (
    <Box sx={{ border: '1px solid #e2ebe4', borderRadius: 3, p: 2.5, backgroundColor: '#fff' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#9aa3ab', letterSpacing: 0.7 }}>
        ONBOARDING PIPELINE
      </Typography>
      <Box sx={{ display: 'flex', mt: 1.5 }}>
        {PIPELINE_STAGES.map((stage, idx) => {
          const done = doneThrough >= 0 && idx <= doneThrough;
          const active = doneThrough >= 0 && idx === doneThrough + 1;
          const sub = subFor(stage.status);
          return (
            <Box key={stage.status} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: done ? GREEN : '#fff',
                  border: active ? `2.5px solid ${GREEN}` : done ? 'none' : '0',
                  ...(done ? {} : active ? {} : { backgroundColor: '#E5E7EB' }),
                }}
              >
                {done && (
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 13, lineHeight: 1 }}>✓</Typography>
                )}
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: 12, color: done || active ? '#1f2937' : '#9aa3ab' }}>
                {stage.label}
              </Typography>
              <Typography sx={{ fontSize: 10, color: active ? GREEN : '#9aa3ab', minHeight: 12 }}>
                {active ? sub || 'Current' : sub}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Current-stage action card ────────────────────────────────────────────────
function ActionCard({
  status,
  busy,
  dataShape,
  setDataShape,
  visitAt,
  setVisitAt,
  onAdvance,
  appId,
}: {
  status: VendorAppStatus;
  busy: boolean;
  dataShape: string;
  setDataShape: (v: string) => void;
  visitAt: string;
  setVisitAt: (v: string) => void;
  onAdvance: (fn: () => Promise<unknown>, ok: string) => void;
  appId: string;
}) {
  const t = (body: Parameters<typeof adminApiClient.transitionVendorApplication>[1], ok: string) =>
    onAdvance(() => adminApiClient.transitionVendorApplication(appId, body), ok);

  let title = '';
  let body = '';
  let controls: React.ReactNode = null;

  switch (status) {
    case 'submitted':
      title = 'Start the review';
      body = 'Move this application into review to begin onboarding.';
      controls = (
        <Button ariaLabel="Start review" buttonText="Start review" variant="contained" size="small" disabled={busy} onClick={() => t({ action: 'start_review' }, 'Review started.')} />
      );
      break;
    case 'in_review':
      title = 'Assign the data shape';
      body = 'Choose how we’ll ingest this vendor’s prices — the price-adapter type.';
      controls = (
        <>
          <Select ariaLabel="Data shape" label="Data shape" native size="small" sx={{ width: 200 }} value={dataShape} onChange={(_e, v) => setDataShape(v as string)} options={DATA_SHAPE_OPTIONS} />
          <Button ariaLabel="Assign data shape" buttonText="Assign data shape" variant="contained" size="small" disabled={busy} onClick={() => t({ action: 'assign_data_shape', dataShape }, 'Data shape assigned.')} />
        </>
      );
      break;
    case 'data_shape_assigned':
      title = 'Schedule the staff visit';
      body = 'Set a date for the onsite visit to capture shelf prices. If an onsite isn’t feasible yet, skip it to advance to “Adapter built.”';
      controls = (
        <>
          <TextField type="date" ariaLabel="Visit date" label="Visit date" size="small" value={visitAt} onChange={(e) => setVisitAt(e.target.value)} sx={{ width: 180 }} />
          <Button ariaLabel="Schedule visit" buttonText="Schedule visit" variant="contained" size="small" disabled={busy || !visitAt} onClick={() => t({ action: 'schedule_visit', visitAt }, 'Visit scheduled.')} />
          <Button ariaLabel="Skip visit" buttonText="Skip visit" variant="outlined" size="small" sx={NEUTRAL_BTN} disabled={busy} onClick={() => t({ action: 'skip_visit' }, 'Visit skipped.')} />
        </>
      );
      break;
    case 'staff_visit_scheduled':
      title = 'Mark the adapter built';
      body = 'Once engineering has wired the price ingestion, mark the adapter built.';
      controls = (
        <Button ariaLabel="Mark adapter built" buttonText="Mark adapter built" variant="contained" size="small" disabled={busy} onClick={() => t({ action: 'mark_adapter_built' }, 'Adapter marked built.')} />
      );
      break;
    case 'adapter_built':
      title = 'Confirm the vendor';
      body = 'Record that the vendor has accepted the contract terms.';
      controls = (
        <Button ariaLabel="Confirm vendor" buttonText="Confirm vendor" variant="contained" size="small" disabled={busy} onClick={() => t({ action: 'confirm_vendor' }, 'Vendor confirmed.')} />
      );
      break;
    case 'vendor_confirmed':
      title = 'Approve onto the marketplace';
      body = 'Approving provisions the Vendor, grants the owner portal access, and goes live. Use the Approve button above.';
      controls = null;
      break;
    default:
      return null;
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3, mt: 2, p: 2.25, borderRadius: 3, backgroundColor: '#F0F9F1', border: '1px solid #CDE8D4', flexWrap: 'wrap' }}>
      <Box sx={{ flex: 1, minWidth: 260 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 11, color: GREEN }}>NEXT STEP</Typography>
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1f2937', my: 0.25 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: '#5a7063' }}>{body}</Typography>
      </Box>
      {controls && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>{controls}</Box>
      )}
    </Box>
  );
}

// ─── Detail panels ────────────────────────────────────────────────────────────
function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 11, color: '#9aa3ab', letterSpacing: 0.3 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: '#374151' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ flex: '1 1 360px', minWidth: 320, border: '1px solid #e2ebe4', borderRadius: 3, p: 2.5, backgroundColor: '#fff', display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>{title}</Typography>
      {children}
    </Box>
  );
}

function BusinessPanel({ app }: { app: VendorApplicationDetail }) {
  const bi: VendorBusinessInfo = app.businessInfo ?? {};
  const addr = bi.address;
  const fullAddr = addr
    ? [addr.line1, addr.line2, [addr.city, addr.state].filter(Boolean).join(', '), addr.postalCode]
        .filter(Boolean)
        .join(', ')
    : '';
  const contact = [bi.contactPhone, bi.contactEmail || app.applicant?.email].filter(Boolean).join('  ·  ');
  const snap = bi.snapAccepted
    ? `Accepts SNAP / EBT${bi.fnsNumber ? ` · FNS ${bi.fnsNumber}` : ''}`
    : 'Not a SNAP retailer';
  const interests = (bi.interests ?? []).join(' · ');

  return (
    <Panel title="Business">
      <Field label="LEGAL NAME" value={bi.businessName} />
      <Field
        label="STORE TYPE"
        value={<StoreChip businessType={bi.businessType} />}
      />
      <Field label="ADDRESS" value={fullAddr} />
      <Field label="CONTACT" value={[bi.contactName, bi.role].filter(Boolean).join(' · ') || contact} />
      {(bi.contactPhone || bi.contactEmail || app.applicant?.email) && <Field label="REACH" value={contact} />}
      {bi.website && <Field label="WEBSITE / SOCIAL" value={bi.website} />}
      {bi.numberOfLocations && <Field label="LOCATIONS" value={bi.numberOfLocations} />}
      <Field label="POS SYSTEM" value={app.posSystem} />
      <Field label="SNAP / EBT" value={snap} />
      {(app.categories?.length ?? 0) > 0 && <Field label="CATEGORIES" value={(app.categories ?? []).join(' · ')} />}
      {interests && <Field label="WHAT MATTERS MOST" value={interests} />}
      {app.motivation && <Field label="MOTIVATION" value={app.motivation} />}
    </Panel>
  );
}

function OnboardingPanel({ app }: { app: VendorApplicationDetail }) {
  const visit = app.staffVisitScheduledAt
    ? `Scheduled ${fmtDate(app.staffVisitScheduledAt)}`
    : stageIndex(app.status) >= 3
      ? 'Skipped'
      : 'Not yet scheduled';
  const c = stageChip(app.status);
  return (
    <Panel title="Onboarding">
      <Field label="CURRENT STAGE" value={<Pill label={c.label} fg={c.fg} bg={c.bg} />} />
      <Field label="DATA SHAPE" value={app.dataShapeAssigned ? app.dataShapeAssigned : 'Not assigned'} />
      <Field label="STAFF VISIT" value={visit} />
      <Field label="PRICE ADAPTER" value={app.adapterBuiltAt ? `Built ${fmtDate(app.adapterBuiltAt)}` : 'Not built'} />
      <Field label="VENDOR CONFIRMATION" value={app.vendorConfirmedAt ? `Confirmed ${fmtDate(app.vendorConfirmedAt)}` : 'Pending'} />
      <Field label="REVIEWER" value={app.reviewerUserId ?? '—'} />
      {app.reviewerDecisionAt && <Field label="DECISION" value={fmtDate(app.reviewerDecisionAt)} />}
      {app.declinedReason && <Field label="DECLINED REASON" value={app.declinedReason} />}
      {app.reapplyUnlockedAt && <Field label="REAPPLICATION UNLOCKED" value={fmtDate(app.reapplyUnlockedAt)} />}
    </Panel>
  );
}
